import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../coreComponents/breadcrumbs";
import ApiService from "../../services/apiService";
import EditForm, { FieldConfig } from "../table/EditForm";
import { routes } from "../../routers/routes";

// ─── Field config ─────────────────────────────────────────────────────────────
 
const USER_FIELDS: FieldConfig[] = [
  { name: "fullName", label: "Full Name", type: "text", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  {
    name: "mobileNumber",
    label: "Mobile Number",
    type: "text",
    required: true,
  },
  {
    name: "role",
    label: "Role",
    type: "select",
    required: true,
    options: [
      { label: "Administrator", value: "Administrator" },
      { label: "User", value: "User" },
    ],
  },
  {
    name: "enabled",
    label: "Status",
    type: "select",
    options: [
      { label: "Active", value: "1" },
      { label: "Inactive", value: "0" },
    ],
  },
  { name: "houseNo", label: "House No", type: "text" },
  { name: "streetLandmark", label: "Street / Landmark", type: "text" },
  { name: "area", label: "Area", type: "text" },
  { name: "village", label: "Village", type: "text" },
  { name: "city", label: "City", type: "text" },
  { name: "district", label: "District", type: "text" },
  { name: "state", label: "State", type: "text" },
  { name: "pincode", label: "Pincode", type: "text" },
  { name: "country", label: "Country", type: "text" },
  {
    name: "createdDateTime",
    label: "Created At",
    type: "datetime",
    disabled: true,
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

const UserEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<Record<string, unknown> | null>(
    null,
  );
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!id) return;
    const fetchUser = async () => {
      setFetching(true);
      try {
        const response = await ApiService.axiosApi(`/users/getuserbyid/${id}`, {
          method: "GET",
        });
        if (response.success) {
          setUserData(response.data);
        } else {
          toast.error(response.message ?? "Could not load user");
        }
      } catch {
        toast.error("Failed to fetch user");  
      } finally {
        setFetching(false); 
      }
    };
    void fetchUser();         
  }, [id]);                    

  const handleSubmit = async (formData: Record<string, unknown>) => {
    const response = await ApiService.axiosApi(`/users/update/${id}`, {
      method: "PUT",
      data: formData,
    });
    if (response.success) {
      toast.success("User updated successfully");
      navigate(routes.AllUsers);
    } else {
      toast.error(response.message ?? "Failed to update user");
      throw new Error(response.message);
    }
  };

  // Show spinner while fetching
  if (fetching || !userData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs mainTitle="Edit User" parent="Users" />
      <Container fluid>
        <Row>
          <Col sm="12">
            {/*
              key={String(userData.id)} — forces EditForm to fully remount
              if somehow a different user's data is loaded (e.g. navigating
              from one edit page to another without unmounting)
            */}
            <EditForm
              key={String(userData.id)}
              title="Edit User"
              fields={USER_FIELDS}
              defaultValues={userData}
              onSubmit={handleSubmit}
              onCancel={() => navigate(-1)}
              columns={2}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default UserEditPage;
